import axios from "axios"
import useAxios, { configure } from "axios-hooks"
import { useMemo } from "react"

const axiosInstanceProxy = axios.create({
  baseURL: process.env.GATSBY_PROXY_URL || "https://preprodproxy.efpa.es/",
})

const requestNewToken = async () => {
  try {
    const { data } = await axiosInstanceProxy.post("/token/")
    if (data?.access) {
      localStorage.setItem("token", data.access)
      if (typeof window !== 'undefined' && window.updateToken) window.updateToken(data.access)
      return data.access
    }
  } catch (_) {}
  return null
}

let tokenRefreshPromise = null
const ensureToken = (force = false) => {
  if (!force) {
    const existing = localStorage.getItem("token")
    if (existing) return Promise.resolve(existing)
  }
  if (!tokenRefreshPromise) {
    tokenRefreshPromise = requestNewToken().finally(() => { tokenRefreshPromise = null })
  }
  return tokenRefreshPromise
}

let globalAxiosInstance = null

if (typeof window !== "undefined" && !globalAxiosInstance) {
  globalAxiosInstance = axios.create({
    baseURL: process.env.GATSBY_API_URL || "https://preprodapi.efpa.es/api/v1/",
  })

  globalAxiosInstance.interceptors.request.use(
    async config => {
      try {
        const token = await ensureToken()
        if (token) {
          config.headers["Authorization"] = "Bearer " + token
        }
        return config
      } catch (_error) {
        return Promise.reject(_error)
      }
    },
    error => Promise.reject(error)
  )

  globalAxiosInstance.interceptors.response.use(
    res => res,
    async err => {
      const originalConfig = err?.config
      
      try {
        const status = err?.response?.status
        const hasToken = !!originalConfig?.headers?.["Authorization"]
        const shouldRetry =
          status === 401 || (status === 403 && !hasToken)

        if (
          err?.response &&
          shouldRetry &&
          !originalConfig._retry
        ) {
          if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
            try {
              window.dispatchEvent(new CustomEvent('http:unauthorized', { detail: { url: originalConfig?.url } }))
            } catch (_) {}
          }
          originalConfig._initialStatus = status
          originalConfig._retry = true
          const access = await ensureToken(true)
          if (access) {
            originalConfig.headers = originalConfig.headers || {}
            originalConfig.headers["Authorization"] = "Bearer " + access
            return globalAxiosInstance(originalConfig)
          }
        }
        return Promise.reject(err)
      } catch (_error) {
        return Promise.reject(_error)
      }
    }
  )

  configure({ axios: globalAxiosInstance })
}

export function useAxiosClient(config, options) {
  const isBrowser = typeof window !== "undefined"
  const newOptions = isBrowser ? options : { manual: true }

  const isValidUrl = useMemo(() => {
    if (!config.url || config.url === null || config.url === undefined) return false
    if (typeof config.url !== 'string' || config.url.trim() === '') return false
    if (config.url.includes('undefined') || config.url.includes('null')) return false
    return true
  }, [config.url])
  
  const fullUrl = useMemo(() => {
    if (!isValidUrl) return null
    try {
      if (config.url.startsWith('http')) return config.url
      const baseURL = process.env.GATSBY_API_URL || "https://preprodapi.efpa.es/api/v1/"
      const cleanUrl = config.url.startsWith('/') ? config.url.slice(1) : config.url
      return `${baseURL}${cleanUrl}`
    } catch (_) {
      return null
    }
  }, [config.url, isValidUrl])

  const memoizedConfig = useMemo(() => {
    if (!isValidUrl || !fullUrl) {
      return {
        method: "get",
        url: null,
        headers: { "Content-Type": "application/json" },
      }
    }
    
    return {
      method: "get",
      ...config,
      url: fullUrl,
      headers: { "Content-Type": "application/json" },
    }
  }, [config, fullUrl, isValidUrl])

  const hookRes = useAxios(memoizedConfig, newOptions)

  if (!isValidUrl) {
    const errorState = {
      data: null,
      loading: false,
      error: {
        message: `URL inválida: ${config.url || 'undefined'}`,
        code: 'INVALID_URL',
        config: config
      }
    }
    return isBrowser ? [errorState, () => {}] : [errorState]
  }

  return isBrowser ? hookRes : [{}]
}

export default axiosInstanceProxy